import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  Save, 
  Eye, 
  Settings,
  Target,
  BarChart3,
  Calendar,
  Users
} from 'lucide-react';
import { QuestionBuilder } from './QuestionBuilder';
import { KPIMapper } from './KPIMapper';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface SurveyBuilderProps {
  survey?: any;
  onClose: () => void;
  onSave: (survey: any) => void;
}

export function SurveyBuilder({ survey, onClose, onSave }: SurveyBuilderProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    survey_type: 'course_evaluation',
    questions: [],
    settings: {
      anonymous: true,
      multiple_responses: false,
      show_progress: true,
      randomize_questions: false,
      required_completion: false
    },
    kpi_mappings: [],
    target_audience: [],
    auto_send_after_course: false,
    reminder_schedule: {
      enabled: false,
      frequency: 'weekly',
      max_reminders: 3
    }
  });
  const [activeTab, setActiveTab] = useState('basic');
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (survey) {
      setFormData({
        title: survey.title || '',
        description: survey.description || '',
        survey_type: survey.survey_type || 'course_evaluation',
        questions: survey.questions || [],
        settings: survey.settings || formData.settings,
        kpi_mappings: survey.kpi_mappings || [],
        target_audience: survey.target_audience || [],
        auto_send_after_course: survey.auto_send_after_course || false,
        reminder_schedule: survey.reminder_schedule || formData.reminder_schedule
      });
    }
  }, [survey]);

  const addQuestion = () => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      type: 'text',
      question: '',
      required: false,
      options: {}
    };
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const updateQuestion = (index: number, question: any) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === index ? question : q)
    }));
  };

  const deleteQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a survey title');
      return;
    }

    if (formData.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    setSaving(true);
    try {
      const surveyData: any = {
        ...formData,
        created_by: user?.id,
        active: true
      };

      // Get user's district_id from profiles
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user?.id)
        .single();

      if (profile?.district_id) {
        surveyData.district_id = profile.district_id;
      }

      let result;
      if (survey?.id) {
        const { data, error } = await supabase
          .from('surveys')
          .update(surveyData)
          .eq('id', survey.id)
          .select()
          .single();
        result = { data, error };
      } else {
        const { data, error } = await supabase
          .from('surveys')
          .insert(surveyData)
          .select()
          .single();
        result = { data, error };
      }

      if (result.error) throw result.error;

      toast.success(`Survey ${survey?.id ? 'updated' : 'created'} successfully!`);
      onSave(result.data);
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error('Failed to save survey');
    } finally {
      setSaving(false);
    }
  };

  const questionTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'rating', label: 'Rating Scale' },
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'checkbox', label: 'Checkboxes' },
    { value: 'yes_no', label: 'Yes/No' },
    { value: 'likert', label: 'Likert Scale' },
    { value: 'matrix', label: 'Matrix/Grid' }
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {survey?.id ? 'Edit Survey' : 'Create New Survey'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="questions">Questions</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="targeting">Targeting</TabsTrigger>
              <TabsTrigger value="kpis">KPIs</TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setPreviewMode(!previewMode)}
                className="flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                Preview
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Survey'}
              </Button>
            </div>
          </div>

          <TabsContent value="basic" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Survey Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Survey Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Course Effectiveness Survey"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the purpose and context of this survey"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="type">Survey Type</Label>
                  <Select
                    value={formData.survey_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, survey_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="course_evaluation">Course Evaluation</SelectItem>
                      <SelectItem value="instructor_feedback">Instructor Feedback</SelectItem>
                      <SelectItem value="kpi_assessment">KPI Assessment</SelectItem>
                      <SelectItem value="general_feedback">General Feedback</SelectItem>
                      <SelectItem value="exit_survey">Exit Survey</SelectItem>
                      <SelectItem value="satisfaction">Satisfaction Survey</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="questions" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Survey Questions</h3>
              <Button onClick={addQuestion} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Question
              </Button>
            </div>

            <div className="space-y-4">
              {formData.questions.map((question, index) => (
                <QuestionBuilder
                  key={question.id}
                  question={question}
                  index={index}
                  onUpdate={(updatedQuestion) => updateQuestion(index, updatedQuestion)}
                  onDelete={() => deleteQuestion(index)}
                  questionTypes={questionTypes}
                />
              ))}
              
              {formData.questions.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">No questions added yet</p>
                    <Button onClick={addQuestion} className="mt-4">
                      Add Your First Question
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Survey Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Anonymous Responses</Label>
                    <p className="text-sm text-muted-foreground">Allow users to respond anonymously</p>
                  </div>
                  <Switch
                    checked={formData.settings.anonymous}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({
                        ...prev,
                        settings: { ...prev.settings, anonymous: checked }
                      }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Multiple Responses</Label>
                    <p className="text-sm text-muted-foreground">Allow users to submit multiple responses</p>
                  </div>
                  <Switch
                    checked={formData.settings.multiple_responses}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({
                        ...prev,
                        settings: { ...prev.settings, multiple_responses: checked }
                      }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Show Progress</Label>
                    <p className="text-sm text-muted-foreground">Display progress bar during survey</p>
                  </div>
                  <Switch
                    checked={formData.settings.show_progress}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({
                        ...prev,
                        settings: { ...prev.settings, show_progress: checked }
                      }))
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-send After Course</Label>
                    <p className="text-sm text-muted-foreground">Automatically send when course is completed</p>
                  </div>
                  <Switch
                    checked={formData.auto_send_after_course}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({
                        ...prev,
                        auto_send_after_course: checked
                      }))
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="targeting" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Target Audience
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Define who should receive this survey. Leave empty to send to all users.
                </p>
                {/* Audience targeting controls would go here */}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="kpis" className="space-y-4">
            <KPIMapper 
              surveyQuestions={formData.questions}
              kpiMappings={formData.kpi_mappings || []}
              onUpdateMappings={(mappings) => 
                setFormData(prev => ({ ...prev, kpi_mappings: mappings }))
              }
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}